import React from "react";
import { Dispatch, SetStateAction } from "react";
import { Gateway } from "pages/ProjectManagement/ProjectManagement.types";
import { SelectOption } from "components/dropdown/Dropdown";
import { customField } from "components/helpers/CustomFieldRenderers/CustomFormFields/model";
import { parseListDate } from "helpers/parseListDate";
import instance from "api";
import { endpoints } from "config";
import { ApiResponse } from "@avamae/formbuilder/dist/FormBuilder";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import { ParamConfig, GatewayClassName } from "./GatewaySummary.types";

/**
 * Parse an array of SelectOption objects into a comma-separated string
 * @param options - Array of SelectOption objects with label property
 * @returns Comma-separated string of labels
 */
export function parseSelectOption(options: SelectOption[]): string {
  return options.map((option) => option.label).join(", ");
}

/**
 * Determine the CSS class name based on gateway completion percentage
 * @param completedTasks - Number of completed tasks
 * @param taskCount - Total number of tasks
 * @param na - Whether gateway is marked as NA
 * @returns CSS class name for styling gateway progress
 */
export const gatewayClass = (
  completedTasks: number,
  taskCount: number,
  na?: boolean
): GatewayClassName => {
  let percentage = (completedTasks / taskCount) * 100;
  if (na) {
    return "NA";
  } else if (percentage === 0 || taskCount === 0) {
    return "Empty";
  } else if (percentage > 0 && percentage < 38) {
    return "Quarter";
  } else if (percentage >= 38 && percentage < 63) {
    return "Half";
  } else if (percentage >= 63 && percentage < 100) {
    return "ThreeQuarter";
  } else {
    return "Full";
  }
};

/**
 * Fetch gateway content for PDF printing
 * @param config - Configuration object with projectId and gatewayId
 * @param setPrintContent - State setter for print content
 */
export const fetchPrintContent = async (
  config: ParamConfig,
  setPrintContent: Dispatch<SetStateAction<Gateway | null>>
): Promise<void> => {
  try {
    const params: any = {
      ProjectsID: config.projectId,
    };

    // Only include GatewayInstancesID if it's a valid number
    if (config.gatewayId != null) {
      params.GatewayInstancesID = config.gatewayId;
    }

    // Only include GatewayTemplateID if it's a valid number
    if (config.gatewayTemplateId != null) {
      params.GatewayTemplateID = config.gatewayTemplateId;
    }

    const result = await instance.get<ApiResponse<Gateway>>(
      endpoints.projectmanagement.getGateway,
      {
        params,
      }
    );
    if (result.data && result.status === 200 && result.data.status === "1" && result.data.details) {
      setPrintContent(result.data.details);
    } else {
      throw new Error("No data found");
    }
  } catch (e) {
    setPrintContent(null);
    toast(
      <ToastMessage
        toastData={[{ title: "Error", text: "Failed to retrieve content" }]}
        className="ToastError"
      />
    );
  }
};

/**
 * Render custom field value based on field type
 * @param field - Custom field object
 * @param summary - Whether to render as summary (div) vs list item
 * @param key - Optional key prop for React elements
 * @returns JSX element for the custom field
 */
export const customFieldRender = (
  customField: customField,
  summary: boolean = false,
  key?: string
) => {
  let displayValue: string | undefined = "";
  let value =
    customField.customFieldType !== "MultiSelect" && customField.customFieldType !== "SingleSelect"
      ? customField.value
      : customField.possibleValuesIds;

  switch (customField.customFieldType) {
    case "Boolean":
      displayValue = value === "True" ? "Yes" : "No";
      break;
    case "String":
    case "Integer":
    case "Float":
    case "Currency":
    case "TextArea":
      displayValue = value as string;
      break;
    case "SingleSelect":
      if (customField.possibleValuesIds == null) {
        break;
      } else if (
        Array.isArray(customField.possibleValuesIds) &&
        customField.possibleValuesIds.length > 0
      ) {
        displayValue = customField.possibleValues.find(
          (o) => o.id === (value as number[])[0]
        )?.value;
      }
      break;
    case "MultiSelect":
      displayValue = (value as number[])
        ?.map((id) => customField.possibleValues.find((o) => o.id === id)?.value)
        .join(", ");
      break;
    case "DateTime":
      displayValue = typeof value === "string" ? parseListDate(value, false) : undefined;
      break;
  }

  return summary ? (
    <div key={key}>{displayValue ? displayValue : "No data"}</div>
  ) : (
    <li key={key}>
      {customField.name} - {displayValue ? displayValue : "No data"}
    </li>
  );
};

/**
 * Check if a gateway can be expanded
 * @param gateway - Gateway object with NA settings
 * @returns True if gateway can be expanded
 */
export const isGatewayExpandable = (gateway: Gateway): boolean => {
  return !gateway.bGatewayNAEnabled || (gateway.bGatewayNAEnabled && !gateway.bGatewayNAValue);
};

/**
 * Check if print button should be visible
 * @param gateway - Gateway object
 * @returns True if print button should be shown
 */
export const isPrintVisible = (gateway: Gateway): boolean => {
  return (
    (gateway.bAllowPrintPDF && !gateway.bGatewayNAEnabled) ||
    (gateway.bAllowPrintPDF && gateway.bGatewayNAEnabled && !gateway.bGatewayNAValue)
  );
};

/**
 * Check if gateway header should be displayed in Overview tab
 * @param tab - Current tab option
 * @param headerGateway - Header gateway task
 * @param gateway - Gateway object
 * @returns True if header should be displayed
 */
export const shouldShowHeaderContent = (
  tab: string,
  headerGateway: any,
  gateway: Gateway
): boolean => {
  return (
    tab === "Overview" &&
    headerGateway &&
    ((gateway.bGatewayNAEnabled && !gateway.bGatewayNAValue) || !gateway.bGatewayNAEnabled)
  );
};

/**
 * Check if header task milestone should be displayed in Gateways tab
 * @param tab - Current tab option
 * @param headerGateway - Header gateway task
 * @param gateway - Gateway object
 * @returns True if header milestone should be displayed
 */
export const shouldShowHeaderMilestone = (
  tab: string,
  headerGateway: any,
  gateway: Gateway
): boolean => {
  return (
    tab === "Gateways" &&
    headerGateway &&
    ((gateway.bGatewayNAEnabled && !gateway.bGatewayNAValue) || !gateway.bGatewayNAEnabled)
  );
};

/**
 * Get CSS class names for gateway container
 * @param gateway - Gateway object
 * @returns Space-separated class names
 */
export const getGatewayContainerClasses = (gateway: Gateway): string => {
  const classes = ["GatewaySummary"];
  if (!gateway.bEditable) classes.push("Disabled");
  if (gateway.bGatewayNAEnabled && gateway.bGatewayNAValue) classes.push("NaTrue");
  return classes.join(" ");
};

/**
 * Get CSS class names for gateway header
 * @param gateway - Gateway object
 * @param tab - Current tab option
 * @returns Space-separated class names
 */
export const getGatewayHeaderClasses = (gateway: Gateway, tab: string): string => {
  const classes = ["GatewayHeader", tab];
  if (gateway.bGatewayNAEnabled && gateway.bGatewayNAValue) classes.push("NaTrue");
  return classes.join(" ");
};

/**
 * Get CSS class names for gateway title container
 * @param gateway - Gateway object
 * @param headerGateway - Header gateway task
 * @returns Space-separated class names
 */
export const getGatewayTitleClasses = (gateway: Gateway, headerGateway: any): string => {
  const classes = ["GatewayTitle"];
  if (!gateway.description) classes.push("NameOnly");
  if (headerGateway) classes.push("HeaderGateway");
  return classes.join(" ");
};

/**
 * Get CSS class names for task container
 * @param expand - Whether gateway is expanded
 * @returns Space-separated class names
 */
export const getTaskContainerClasses = (expand: boolean): string => {
  return `TaskContainer ${expand ? "expanded" : ""}`;
};

/**
 * Get CSS class names for chevron icon
 * @param expand - Whether gateway is expanded
 * @param gateway - Gateway object
 * @returns Space-separated class names
 */
export const getChevronClasses = (expand: boolean, gateway: Gateway): string => {
  const classes = ["Chevron"];
  if (expand) classes.push("ChevronExpand");
  if (gateway.bGatewayNAEnabled && gateway.bGatewayNAValue) classes.push("ChevronHide");
  return classes.join(" ");
};
